
import React, { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { CheckCircle, Image } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useIsMobile } from "@/hooks/use-mobile";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { PropertyImage } from "@/types/property";

interface SimpleImageGalleryProps {
  images: PropertyImage[];
  onImagesChange: (images: PropertyImage[]) => void;
  maxSelected: number;
}

const SimpleImageGallery = ({ images, onImagesChange, maxSelected }: SimpleImageGalleryProps) => {
  const selectedCount = images.filter(img => img.selected).length;
  const isMobile = useIsMobile();
  const [isScrolled, setIsScrolled] = useState(false);

  const toggleImageSelection = (imageId: string) => {
    const image = images.find(img => img.id === imageId);

    // If trying to select more than maxSelected, prevent selection
    if (!image?.selected && selectedCount >= maxSelected) {
      return;
    }

    const updatedImages = images.map(img => {
      if (img.id === imageId) {
        return { ...img, selected: !img.selected };
      }
      return img;
    });

    onImagesChange(updatedImages);
  };

  useEffect(() => {
    if (isMobile) {
      const handleScroll = () => {
        const scrollPosition = window.scrollY;
        setIsScrolled(scrollPosition > 50);
      };

      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [isMobile]);

  return (
    <div className="space-y-3">
      <div className={cn(
        "flex justify-between items-center mb-2 bg-background/80 backdrop-blur-sm",
        isMobile ? "sticky top-[60px] z-10" : "sticky top-0 z-10"
      )}>
        <h3 className="text-lg font-medium">Property Images</h3>
        <div className="text-sm font-medium">
          <Badge variant="secondary" className="px-3 py-1">
            Selected: {selectedCount}/{maxSelected}
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 mt-1">
        {images.map(image => (
          <div
            key={image.id}
            className={cn(
              "relative rounded-lg overflow-hidden border-2 cursor-pointer transition-all duration-200",
              image.selected
                ? "ring-2 ring-primary border-primary"
                : "border-transparent hover:border-muted-foreground/20"
            )}
            onClick={() => toggleImageSelection(image.id)}
          >
            <AspectRatio ratio={1} className="w-full">
              <img
                src={image.url}
                alt="Property"
                className={cn(
                  "w-full h-full object-cover",
                  !image.selected && "opacity-80"
                )}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.onerror = null;
                  target.src = 'https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=600&h=400&fit=crop&q=80';
                  target.classList.add('hidden');
                  console.log('broken image', image.url);
                }}
              />
              {image.selected && (
                <div className="absolute top-2 right-2 bg-primary rounded-full text-white p-0.5">
                  <CheckCircle className="h-4 w-4" />
                </div>
              )}
              {!image.selected && (
                <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 bg-black/30 transition-opacity">
                  <div className="bg-white/90 rounded-full p-1">
                    <Image className="h-5 w-5 text-primary" />
                  </div>
                </div>
              )}
            </AspectRatio>
          </div>
        ))}
      </div>

      {isMobile && (
        <div className={cn(
          "fixed bottom-6 left-1/2 -translate-x-1/2 z-50 transition-all duration-300",
          isScrolled ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        )}>
          <Badge
            variant="default"
            className="px-4 py-2 text-md font-medium shadow-lg bg-primary/90 backdrop-blur-sm"
          >
            {selectedCount}/{maxSelected} Images Selected
          </Badge>
        </div>
      )}

      <p className="text-sm text-muted-foreground mt-2">
        Click to select/deselect images. Order doesn't matter.
      </p>
    </div>
  );
};

export default SimpleImageGallery;
